import {ChevronDownIcon} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import React, {Children, ComponentProps, ReactNode, useCallback, useRef} from 'react';

import Col from '../../../common/Col';
import {useBreakpoint} from '../../../common/util/useBreakpoint';
import NormalButton from './NormalButton';

type DropdownProps = ComponentProps<typeof NormalButton> & {
  buttonIcon: ReactNode;
  buttonLine1: string;
  buttonLine2?: string;
  verticalLineCentered?: boolean;
  children: ReactNode;
};

const Dropdown = ({
  id,
  buttonIcon,
  buttonLine1,
  buttonLine2,
  verticalLineCentered,
  children,
  className,
  style,
}: DropdownProps) => {
  const {isMobile} = useBreakpoint('mobile');
  const menuRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback(() => {
    if (menuRef.current?.contains(document.activeElement)) {
      setTimeout(() => {
        // Blur element on next cycle, after it has gained focus
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      }, 0);
    }
  }, []);

  const arrayChildren = Children.toArray(children);

  return (
    <div className="flex group relative justify-end" ref={menuRef}>
      <NormalButton
        id={id}
        className={classNames(
          isMobile ? '!p-4' : 'radius-[8rem]',
          'focus-within:shadow-none focus-within:translate-y-2 focus-within:-translate-x-2',
          className
        )}
        onMouseDown={handleMouseDown}
        left={
          !isMobile &&
          buttonIcon && (
            <div className="flex ml-2 my-0.5 h-[4.5rem] w-[4.5rem] align-center items-center">{buttonIcon}</div>
          )
        }
        right={
          !isMobile && (
            <>
              <ChevronDownIcon className="flex w-14 h-14 text-white group-focus-within:-scale-y-100 transition-transform duration-300" />
            </>
          )
        }
        style={style}
      >
        <Col className="gap-1 text-base whitespace-nowrap">
          {isMobile && buttonIcon ? (
            buttonIcon
          ) : (
            <>
              <div>{buttonLine1}</div>
              {buttonLine2 && <div>{buttonLine2}</div>}
            </>
          )}
        </Col>
      </NormalButton>
      <div
        className={classNames(
          'absolute hidden group-focus-within:flex flex-col space-y-6 flex-1 items-end overflow-visible z-[1]',
          isMobile ? 'right-32' : 'right-0 top-[9.9rem]'
        )}
      >
        {arrayChildren.map((child, idx) => (
          <div key={`help-child-${idx}`} className="relative">
            {isMobile && idx === 0 ? (
              <div className="absolute w-8 h-2 top-1/2 -right-8 bg-primary-300" />
            ) : (
              <div
                className={classNames(
                  'absolute w-2 h-6 -top-6 bg-primary-300',
                  verticalLineCentered ? 'right-1/2' : 'right-20'
                )}
              />
            )}
            {child}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Dropdown;
